import { COMMPASS_PAYOUT_PCT, COMPLETE_REASONS, DISCOVERY_DOC_LABEL, DOC_REQUEST_TYPES, FLOW, LOB_NAME, STATUS, TIERS, gradeSubmission, type GatekeeperCarrier, type SubmissionData, type Message, type SubmissionStatus, type TierKey } from '@commpass/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { currencyInput, money, parseMoney } from '../lib/format';
import { BackIcon, DownloadIcon, SendIcon, SparkIcon } from '../components/Icons';
import { Messages } from '../components/Messages';
import { ExtractPanel, GatekeeperPanel, PaymentPanel, ProposalPanel, QuotesPanel } from '../components/panels';
import { Pipeline } from '../components/Pipeline';
import { StatusPill } from '../components/StatusPill';
import { useToast } from '../components/Toast';
import { useAuth } from '../auth/AuthProvider';
import { useGatekeeperCarriers, useSubmission, useUpdateSubmission } from '../data/submissions';
import { ApiError, api } from '../lib/api';
import { Modal } from '../components/Modal';
import { MgaModal } from '../components/MgaModal';
import { GatekeeperEmailModal } from '../components/GatekeeperEmailModal';
import { supabase } from '../lib/supabase';

type DetailField = {
  key: string;
  label: string;
  area?: boolean;
  displayOnly?: boolean; // shown in the read-only view but not the edit form (e.g. Lines)
  editOnly?: boolean; // editable + shown in its own panel, so kept out of the rows list
  render?: (d: Record<string, unknown>) => string; // custom read-only formatting
};

// Single source of truth for the insured/business fields shown AND edited on the
// staff detail. Keeping display + edit in one list stops them drifting apart —
// that drift is exactly why the insured phone/email were captured but never shown.
const INSURED_FIELDS: DetailField[] = [
  { key: 'agencyName', label: 'Agency' },
  { key: 'producer', label: 'Producer' },
  { key: 'submitterPhone', label: 'Submitter phone' },
  { key: 'submitterEmail', label: 'Submitter email' },
  { key: 'businessName', label: 'Business name' },
  { key: 'primaryContact', label: 'Primary contact' },
  { key: 'phone', label: 'Insured phone' },
  { key: 'email', label: 'Insured email' },
  { key: 'physicalAddress', label: 'Physical address' },
  { key: 'mailingAddress', label: 'Mailing address' },
  { key: 'website', label: 'Website' },
  { key: 'fein', label: 'FEIN' },
  { key: 'yearsInBusiness', label: 'Years in business' },
  { key: 'sicNaics', label: 'SIC / NAICS' },
  { key: 'amsNumber', label: 'AMS360 #' },
  {
    key: 'lines',
    label: 'Lines',
    displayOnly: true,
    render: (d) => ((d.lines as string[]) ?? []).map((l) => LOB_NAME[l] ?? l).join(', '),
  },
  { key: 'incumbentCarrier', label: 'Incumbent carrier' },
  { key: 'policyExpiration', label: 'Policy expiration' },
  { key: 'quoteNeedBy', label: 'Quote need-by' },
  { key: 'targetPricing', label: 'Target pricing' },
  { key: 'midTermReason', label: 'Mid-term reason' },
  { key: 'grossAnnualRevenue', label: 'Gross annual revenue' },
  { key: 'annualPayroll', label: 'Annual payroll' },
  { key: 'referredBy', label: 'Referred by' },
  { key: 'additionalDetails', label: 'Additional details', area: true },
  { key: 'knownLosses', label: 'Known losses', area: true },
  { key: 'descriptionOfOps', label: 'Description of operations', area: true, editOnly: true },
];
const EDITABLE_FIELDS = INSURED_FIELDS.filter((f) => !f.displayOnly);

// The catch-all "Other details" panel shows every remaining non-empty scalar the
// agent sent, so a field can never be captured-but-invisible again — even a brand
// new one added later. These keys are skipped because they're shown elsewhere on
// the page or are internal plumbing / staff-entered pipeline data.
const CATCHALL_SKIP = new Set<string>([
  ...INSURED_FIELDS.map((f) => f.key),
  'id', 'tier', 'status', 'prevStatus', 'unread', 'createdAt', 'lastAgentEditAt',
  'completedAt', 'business_name', 'agencyMemberId', 'agencyAccountId',
  'isRenewal', 'isMidTerm', 'executiveSummary',
  'documents', 'quotes', 'statusHistory', 'tierHistory', 'messages', 'internalNotes',
  'gatekeeperCarriers', 'docRequests', 'extracted', 'linkedMember', 'proposalDoc',
  'billPlanOptions', 'proposalPremium', 'boundPremium', 'commissionPct',
  'effectiveDate', 'chosenBillPlan', 'downPayment', 'proposalNote', 'paymentNote',
  'mgaName',
]);
const CATCHALL_LABELS: Record<string, string> = {
  inAms: 'Entered in AMS360?',
  accountType: 'Account type',
  quoteTiming: 'Quote timing',
};
const humanizeKey = (k: string) => k.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/^./, (c) => c.toUpperCase());

// Stages a specialist can set by hand (everything except the agent-invite origin state).
const STAGE_CHOICES: SubmissionStatus[] = (Object.keys(STATUS) as SubmissionStatus[]).filter((s) => s !== 'invited');

const pctInput = (v: string) => v.replace(/[^0-9.]/g, '').slice(0, 6);

// File → base64 (chunked, so large files don't blow the call stack).
async function fileToBase64(file: File): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...Array.from(bytes.subarray(i, i + chunk)));
  }
  return btoa(binary);
}

export function SubmissionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { isStaff } = useAuth();
  const { data: sub, isLoading, error } = useSubmission(id);
  const update = useUpdateSubmission();
  const qc = useQueryClient();
  // Staff-only underwriter contacts, fetched from the backend for Gatekeeper + Liberty deals.
  const reqTier = (sub?.tier as string) || (sub?.data?.tier as string);
  const gkCarriers = useGatekeeperCarriers(reqTier === 'gatekeeper' || reqTier === 'liberty');
  const [msgBusy, setMsgBusy] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [noteBusy, setNoteBusy] = useState(false);
  const [uploadBusy, setUploadBusy] = useState(false);
  const uploadInputRef = useRef<HTMLInputElement | null>(null);
  const [pinConfirm, setPinConfirm] = useState(false);
  const [pinBusy, setPinBusy] = useState(false);
  const [newPin, setNewPin] = useState<string | null>(null);
  const [showComplete, setShowComplete] = useState(false);
  const [compReason, setCompReason] = useState('');
  const [compNote, setCompNote] = useState('');
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<Record<string, string>>({});
  const [showDocReq, setShowDocReq] = useState(false);
  const [reqSel, setReqSel] = useState<string[]>([]);
  const [reqNote, setReqNote] = useState('');
  const [showMga, setShowMga] = useState(false);
  // Email carriers chosen on a Gatekeeper submission — opens the batch submission mailer.
  const [gkEmail, setGkEmail] = useState<GatekeeperCarrier[] | null>(null);
  const [narrative, setNarrative] = useState<{
    descriptionOfOps: string;
    executiveSummary: string;
    verification?: { claim: string; note: string }[];
  } | null>(null);
  const [aiBusy, setAiBusy] = useState(false);
  // Bound figures that feed the COMM-PASS commission book. Loaded once per submission.
  const [bound, setBound] = useState({ boundPremium: '', commissionPct: '', effectiveDate: '' });
  const boundLoadedFor = useRef<string | null>(null);

  // Opening a submission clears the shared unread flag (so the queue glow turns off
  // for the whole team). Runs once per mount so a later "Mark as unread" isn't undone.
  const clearedUnread = useRef(false);
  useEffect(() => {
    if (!sub || clearedUnread.current) return;
    clearedUnread.current = true;
    if (sub.data?.unread) {
      void update.mutateAsync({ id: sub.id, data: { ...(sub.data as SubmissionData), unread: false } });
    }
  }, [sub, update]);

  // Populate the bound-figures form from the submission (once per submission id).
  useEffect(() => {
    if (!sub || boundLoadedFor.current === sub.id) return;
    boundLoadedFor.current = sub.id;
    setBound({
      boundPremium: (sub.data.boundPremium as string) || '',
      commissionPct: (sub.data.commissionPct as string) || '',
      effectiveDate: (sub.data.effectiveDate as string) || '',
    });
  }, [sub]);

  if (!isStaff) {
    return (
      <div className="wrap">
        <div className="empty">
          <h3>Staff sign-in required</h3>
          <p>Sign in with your Agents Alliance account to view submission details.</p>
        </div>
      </div>
    );
  }

  if (isLoading) return <div className="wrap"><div className="empty"><p>Loading…</p></div></div>;
  if (error || !sub)
    return (
      <div className="wrap">
        <div className="empty">
          <h3>Not found</h3>
          <p>{error ? (error as Error).message : 'That submission does not exist.'}</p>
        </div>
      </div>
    );

  const d = sub.data;
  const grade = gradeSubmission(d); // staff-only completeness grade
  const curIdx = FLOW.indexOf(sub.status as SubmissionStatus);
  const nextStatus = curIdx >= 0 && curIdx < FLOW.length - 1 ? FLOW[curIdx + 1] : null;
  // Where "Resume" sends an Info-Needed submission: back to the stage it was on.
  const resumeTo = ((d.prevStatus as string) || 'quoting') as SubmissionStatus;
  const submissionTier = (sub.tier as string) || (d.tier as string);
  const isGatekeeper = submissionTier === 'gatekeeper';
  const isLiberty = submissionTier === 'liberty';

  // Merge a partial into data, optionally changing status (appends to statusHistory).
  async function patch(partial: Partial<SubmissionData>, status?: string) {
    if (!sub) return;
    const history = Array.isArray(d.statusHistory) ? d.statusHistory : [];
    const nextData: SubmissionData = { ...d, ...partial };
    if (status) {
      nextData.status = status;
      nextData.statusHistory = [...history, { status, at: Date.now() }];
    }
    await update.mutateAsync({ id: sub.id, status, data: nextData });
  }

  async function advance(to: SubmissionStatus) {
    await patch({}, to);
    toast(`Moved to ${STATUS[to].label}`);
  }

  // Manually set any stage (fix a too-early advance, back up a step, etc.).
  async function setStage(to: string) {
    if (!sub || to === sub.status) return;
    // Preserve Resume behavior when manually dropping to Info Needed.
    const partial: Partial<SubmissionData> = to === 'info_needed' ? { prevStatus: sub.status } : {};
    await patch(partial, to);
    toast(`Stage set to ${STATUS[to as SubmissionStatus]?.label ?? to}`);
  }

  // Remember the current stage so "Resume" can return here.
  async function markInfoNeeded() {
    if (!sub) return;
    await patch({ prevStatus: sub.status }, 'info_needed');
    toast('Marked info needed');
  }

  // Re-flag as unread so the queue glows for the whole team (e.g. hand off to Cameron).
  async function markUnread() {
    if (!sub) return;
    await patch({ unread: true });
    toast('Flagged as unread — it will glow in the queue for the team');
  }

  // Mark the submission completed from here (no need to find it on the board). Matches
  // the board: a bound deal completes outright; anything else records a reason.
  function onCompleteClick() {
    if (!sub) return;
    if (sub.status === 'bound') void completeSubmission();
    else {
      setCompReason('');
      setCompNote('');
      setShowComplete(true);
    }
  }
  async function completeSubmission(reason?: string) {
    if (!sub) return;
    const now = Date.now();
    const partial: Partial<SubmissionData> = { completedAt: now };
    if (reason) {
      partial.completedReason = reason;
      const history = Array.isArray(d.statusHistory) ? d.statusHistory : [];
      partial.statusHistory = [...history, { status: sub.status, at: now, note: `Completed: ${reason}` }];
    }
    await patch(partial);
    setShowComplete(false);
    toast('Marked completed — it moves to the board’s Completed lane');
  }
  async function reopenSubmission() {
    if (!sub) return;
    await patch({ completedAt: undefined, completedReason: undefined });
    toast('Reopened — back in the active queue');
  }

  function startEdit() {
    setForm(
      Object.fromEntries(EDITABLE_FIELDS.map((f) => [f.key, (d[f.key as keyof typeof d] as string) || ''])),
    );
    setEditing(true);
  }

  async function saveEdit() {
    // Patch every editable field from the one canonical list — no hand-kept subset
    // to fall out of sync with what's shown/edited.
    await patch(
      Object.fromEntries(EDITABLE_FIELDS.map((f) => [f.key, form[f.key] ?? ''])) as Partial<SubmissionData>,
    );
    setEditing(false);
    toast('Submission updated');
  }

  async function submitDocRequest() {
    if (!sub || reqSel.length === 0) return;
    const now = Date.now();
    const items = reqSel.map((label, i) => ({ id: `${now}-${i}`, label, note: reqNote || undefined, at: now }));
    const existing = Array.isArray(d.docRequests) ? d.docRequests : [];
    await patch({ docRequests: [...existing, ...items] });
    try {
      const r = await api.requestDocumentNotify({ submissionId: sub.id, labels: reqSel, note: reqNote || undefined });
      toast(r.emailed ? 'Requested — agent emailed' : 'Document(s) requested');
    } catch (e) {
      toast(e instanceof ApiError ? e.message : 'Requested (email could not be sent)');
    }
    setShowDocReq(false);
    setReqSel([]);
    setReqNote('');
  }

  async function openReqDoc(path?: string) {
    if (!path) return;
    const { data: signed } = await supabase.storage.from('submission-docs').createSignedUrl(path, 60);
    if (signed?.signedUrl) window.open(signed.signedUrl, '_blank');
  }

  async function sendMessage(text: string) {
    if (!sub) return;
    setMsgBusy(true);
    try {
      await api.postStaffMessage(sub.id, text);
      await qc.invalidateQueries({ queryKey: ['submission', sub.id] });
      toast('Message sent — agent notified by email');
    } catch (e) {
      toast(e instanceof ApiError ? e.message : 'Could not send message');
    } finally {
      setMsgBusy(false);
    }
  }

  async function addNote() {
    if (!sub || !noteText.trim()) return;
    setNoteBusy(true);
    try {
      await api.addInternalNote(sub.id, noteText.trim());
      setNoteText('');
      await qc.invalidateQueries({ queryKey: ['submission', sub.id] });
    } catch (e) {
      toast(e instanceof ApiError ? e.message : 'Could not save note');
    } finally {
      setNoteBusy(false);
    }
  }

  // Staff upload: attach one or more files straight to this submission (emailed-in
  // docs, or a .zip the agent sent that we unzipped ourselves). Each lands like an
  // agent upload and feeds the AI narrative + MGA/UW emails.
  async function uploadStaffDocs(files: FileList | null) {
    if (!sub || !files || files.length === 0) return;
    setUploadBusy(true);
    let ok = 0;
    try {
      for (const file of Array.from(files)) {
        try {
          const data = await fileToBase64(file);
          await api.staffUploadDoc({
            submissionId: sub.id,
            fileName: file.name,
            mime: file.type || 'application/octet-stream',
            data,
          });
          ok++;
        } catch (e) {
          const isZip = /\.zip$/i.test(file.name) || /zip/i.test(file.type);
          toast(
            isZip
              ? `${file.name}: unzip it first — attach the individual files, not the .zip.`
              : `${file.name}: ${e instanceof ApiError ? e.message : 'upload failed'}`,
          );
        }
      }
      if (ok) {
        await qc.invalidateQueries({ queryKey: ['submission', sub.id] });
        toast(`Added ${ok} document${ok === 1 ? '' : 's'}`);
      }
    } finally {
      setUploadBusy(false);
      if (uploadInputRef.current) uploadInputRef.current.value = '';
    }
  }

  // Staff: reset this submission's login PIN and show the new one to relay to the agent.
  async function doResetPin() {
    if (!sub) return;
    setPinBusy(true);
    try {
      const r = await api.resetPin(sub.id);
      setPinConfirm(false);
      setNewPin(r.pin);
    } catch (e) {
      toast(e instanceof ApiError ? e.message : 'Could not reset PIN');
    } finally {
      setPinBusy(false);
    }
  }

  async function syncSalesforce() {
    if (!sub) return;
    try {
      await api.syncSalesforce('submission', sub.id);
      toast('Sent to Salesforce');
    } catch (e) {
      toast(e instanceof ApiError ? e.message : 'Sync failed');
    }
  }

  // One button: pull the company website (if set) + uploaded documents + account facts
  // and draft BOTH a cohesive Description of Operations and an executive summary. Opens
  // in a review box with both sections editable — nothing is saved until staff approve.
  async function generateNarrative() {
    if (!sub) return;
    setAiBusy(true);
    try {
      const r = await api.aiNarrative(sub.id);
      if (r.unreadable || (!r.descriptionOfOps && !r.executiveSummary)) {
        toast('Not enough source material — add a website or documents, or write it manually.');
        return;
      }
      setNarrative({
        descriptionOfOps: r.descriptionOfOps || ((d.descriptionOfOps as string) || ''),
        executiveSummary: r.executiveSummary || ((d.executiveSummary as string) || ''),
        verification: r.verification,
      });
    } catch (e) {
      // ApiError carries the server's real message (busy / timed out / not configured).
      // A non-ApiError is a network drop or client-side timeout — say so, don't claim
      // the AI is "not configured".
      toast(e instanceof ApiError ? e.message : 'The AI request failed or timed out — please try again.');
    } finally {
      setAiBusy(false);
    }
  }

  async function saveNarrative() {
    if (!narrative) return;
    await patch({
      descriptionOfOps: narrative.descriptionOfOps,
      executiveSummary: narrative.executiveSummary,
      narrativeGeneratedAt: Date.now(),
    } as Partial<SubmissionData>);
    toast('Saved');
    setNarrative(null);
  }

  // Save the canonical bound premium + commission % + effective date (feeds the
  // COMM-PASS commission book on /solutions).
  async function saveBound() {
    await patch({
      boundPremium: bound.boundPremium,
      commissionPct: bound.commissionPct,
      effectiveDate: bound.effectiveDate,
    });
    toast('Commission details saved');
  }

  const dd = d as Record<string, unknown>;
  const narrativeGenAt = Number(dd.narrativeGeneratedAt) || 0; // when the ops/exec summary was last generated
  const rows: [string, string][] = INSURED_FIELDS.filter((f) => !f.editOnly).map((f) => {
    const raw = f.render ? f.render(dd) : ((dd[f.key] as string) ?? '');
    const val = (raw ?? '').toString();
    return [f.label, val.trim() ? val : '—'];
  });

  // Safety net: any non-empty scalar the agent sent that isn't shown in a dedicated
  // spot. Guarantees nothing an agent enters is ever silently invisible to staff.
  const otherProvided: [string, string][] = Object.entries(dd)
    .filter(([k, v]) => !CATCHALL_SKIP.has(k) && v != null && typeof v !== 'object' && String(v).trim().length > 0)
    .map(([k, v]) => [CATCHALL_LABELS[k] ?? humanizeKey(k), String(v)]);

  // Timeline merges status changes and manual tier reassignments, newest first.
  // The original submission date anchors the bottom so staff can always see the date
  // it actually came in through the portal.
  const tierName = (k?: string) => (k && TIERS[k as TierKey] ? TIERS[k as TierKey].name : 'Unassigned');
  const receivedAt =
    Number(d.createdAt) || (sub.created_at ? Date.parse(sub.created_at) : 0) || 0;
  const timeline: { at: number; label: string; sub?: string }[] = [
    ...(Array.isArray(d.statusHistory) ? d.statusHistory : []).map((h) => ({
      at: h.at,
      label: STATUS[h.status as SubmissionStatus]?.label ?? h.status,
      sub: h.note || undefined, // e.g. a completion reason or the auto-close note
    })),
    ...(Array.isArray(d.tierHistory) ? d.tierHistory : []).map((t) => ({
      at: t.at,
      label: 'Tier reassigned',
      sub: `${tierName(t.from)} → ${tierName(t.to)}`,
    })),
    ...(receivedAt ? [{ at: receivedAt, label: 'Submission received', sub: 'Came in through the portal' }] : []),
  ].sort((a, b) => b.at - a.at);

  // Live commission preview for the bound-figures panel (fixed 30% producer share).
  const boundTotalComm = (parseMoney(bound.boundPremium) * (parseFloat(bound.commissionPct) || 0)) / 100;
  const boundYouMake = (boundTotalComm * COMMPASS_PAYOUT_PCT) / 100;

  return (
    <div className="wrap">
      <div className="back-link" onClick={() => navigate('/queue')}>
        <BackIcon width={16} height={16} /> Back to queue
      </div>

      <div className="page-head">
        <div>
          <div className="page-title">{sub.business_name || 'Submission'}</div>
          <div className="page-sub">
            {sub.id} · {(d.agencyName as string) || ''}
          </div>
        </div>
        <StatusPill status={sub.status} reviewer />
      </div>

      <div className="panel" style={{ marginBottom: 20 }}>
        <div className="panel-pad">
          <Pipeline status={sub.status} />
        </div>
      </div>

      <div className="panel" style={{ marginBottom: 20, borderLeft: '3px solid #B7791F' }}>
        <div className="panel-head" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0 }}>Internal notes</h3>
          <span
            style={{ fontSize: 11, fontWeight: 700, color: '#8a5a00', background: '#FDF3D7', border: '1px solid #E7CE8A', borderRadius: 999, padding: '2px 8px' }}
          >
            AAS staff only — not visible to the agent
          </span>
        </div>
        <div className="panel-pad">
          {((d.internalNotes ?? []).length === 0) ? (
            <div className="sub-meta" style={{ marginBottom: 10 }}>
              No internal notes yet. Use this to coordinate with the team — e.g. "Emailed the BITCO UW 8/25, awaiting
              reply" — so no one duplicates an outreach.
            </div>
          ) : (
            [...(d.internalNotes ?? [])]
              .sort((a, b) => a.at - b.at)
              .map((n) => (
                <div key={n.id} style={{ padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                  <div style={{ whiteSpace: 'pre-wrap', fontSize: 14, lineHeight: 1.5, textAlign: 'left' }}>{n.text}</div>
                  <div className="sub-meta" style={{ marginTop: 3 }}>
                    {n.author || 'AAS'} · {new Date(n.at).toLocaleString()}
                  </div>
                </div>
              ))
          )}
          <textarea
            className="finput"
            rows={2}
            style={{ marginTop: 10 }}
            placeholder="Add a note for the team…"
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
          />
          <button
            className="btn btn-ghost btn-sm"
            style={{ marginTop: 8 }}
            disabled={noteBusy || !noteText.trim()}
            onClick={() => void addNote()}
          >
            {noteBusy ? 'Saving…' : 'Add internal note'}
          </button>
        </div>
      </div>

      <div className="detail-grid">
        <div>
          <div className="panel">
            <div className="panel-head">
              <h3>Submission detail</h3>
              {!editing && (
                <button className="btn btn-ghost btn-sm" onClick={startEdit}>
                  Edit
                </button>
              )}
            </div>
            <div className="panel-pad">
              {editing ? (
                <div style={{ display: 'grid', gap: 10 }}>
                  {EDITABLE_FIELDS.map((f) => (
                    <div key={f.key} className="fgroup" style={{ margin: 0 }}>
                      <label className="flabel">{f.label}</label>
                      {f.area ? (
                        <textarea
                          className="finput"
                          rows={2}
                          value={form[f.key] ?? ''}
                          onChange={(e) => setForm((s) => ({ ...s, [f.key]: e.target.value }))}
                        />
                      ) : (
                        <input
                          className="finput"
                          value={form[f.key] ?? ''}
                          onChange={(e) => setForm((s) => ({ ...s, [f.key]: e.target.value }))}
                        />
                      )}
                    </div>
                  ))}
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn btn-primary btn-sm" onClick={() => void saveEdit()} disabled={update.isPending}>
                      {update.isPending ? 'Saving…' : 'Save'}
                    </button>
                    <button className="btn btn-ghost btn-sm" onClick={() => setEditing(false)}>
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                rows.map(([label, value]) => (
                  <div
                    key={label}
                    style={{ display: 'flex', justifyContent: 'space-between', gap: 16, padding: '8px 0', borderBottom: '1px solid var(--border)' }}
                  >
                    <span style={{ color: 'var(--muted)', fontSize: 13 }}>{label}</span>
                    <span style={{ fontWeight: 500, fontSize: 14, textAlign: 'right' }}>{value}</span>
                  </div>
                ))
              )}
            </div>
          </div>

          {otherProvided.length > 0 && (
            <div className="panel">
              <div className="panel-head">
                <h3>Other details the agent provided</h3>
              </div>
              <div className="panel-pad">
                {otherProvided.map(([label, value]) => (
                  <div
                    key={label}
                    style={{ display: 'flex', justifyContent: 'space-between', gap: 16, padding: '8px 0', borderBottom: '1px solid var(--border)' }}
                  >
                    <span style={{ color: 'var(--muted)', fontSize: 13 }}>{label}</span>
                    <span style={{ fontWeight: 500, fontSize: 14, textAlign: 'right', whiteSpace: 'pre-wrap' }}>{value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="panel">
            <div className="panel-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3>Documents</h3>
              <button
                className="btn btn-ghost btn-sm"
                disabled={uploadBusy}
                onClick={() => uploadInputRef.current?.click()}
                title="Attach files that reached us outside the portal (emailed in, or an unzipped bundle)"
              >
                {uploadBusy ? 'Uploading…' : 'Upload documents'}
              </button>
              <input
                ref={uploadInputRef}
                type="file"
                multiple
                hidden
                onChange={(e) => void uploadStaffDocs(e.target.files)}
              />
            </div>
            <div className="panel-pad">
              {(d.documents ?? []).length === 0 ? (
                <div className="sub-meta">
                  No documents attached to this submission yet. Use <b>Upload documents</b> to add files that
                  reached us outside the portal, or “Request a document” to ask the agent to upload one.
                </div>
              ) : (
                (d.documents ?? []).map((doc) => (
                  <div
                    key={doc.id}
                    style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--border)' }}
                  >
                    <div>
                      <b>{doc.label || doc.fileName || doc.kind}</b>
                      {doc.storagePath && doc.fileName && doc.fileName !== doc.label && (
                        <div className="sub-meta">{doc.fileName}</div>
                      )}
                      {!doc.storagePath && doc.skipReason && (
                        <div className="sub-meta">Not provided — {doc.skipReason}</div>
                      )}
                      {!doc.storagePath && !doc.skipReason && <div className="sub-meta">Not attached</div>}
                    </div>
                    {doc.storagePath && (
                      <button className="reset-link" onClick={() => void openReqDoc(doc.storagePath)}>
                        Open
                      </button>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>

          {(d.docRequests ?? []).length > 0 && (
            <div className="panel">
              <div className="panel-head">
                <h3>Document requests</h3>
              </div>
              <div className="panel-pad">
                {(d.docRequests ?? []).map((r) => (
                  <div
                    key={r.id}
                    style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '8px 0', borderBottom: '1px solid var(--border)' }}
                  >
                    <div>
                      <b>{r.label}</b>
                      {r.note && <div className="sub-meta">{r.note}</div>}
                    </div>
                    {r.storagePath ? (
                      <button className="reset-link" title="Open" onClick={() => void openReqDoc(r.storagePath)}>
                        ✓ {r.docName || 'Uploaded'}
                      </button>
                    ) : (
                      <span className="sub-meta">Outstanding</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="panel">
            <div className="panel-head">
              <h3>Description of operations</h3>
            </div>
            <div className="panel-pad" style={{ whiteSpace: 'pre-wrap', fontSize: 14, lineHeight: 1.55, textAlign: 'left' }}>
              {((d.descriptionOfOps as string) || '').trim() ? (d.descriptionOfOps as string) : <span className="sub-meta">Not provided yet.</span>}
            </div>
          </div>

          <div className="panel">
            <div className="panel-head">
              <h3>Executive summary</h3>
            </div>
            <div className="panel-pad" style={{ whiteSpace: 'pre-wrap', fontSize: 14, lineHeight: 1.55, textAlign: 'left' }}>
              {((d.executiveSummary as string) || '').trim() ? (
                d.executiveSummary as string
              ) : (
                <span className="sub-meta">Not generated yet — use “Generate ops &amp; exec summary”.</span>
              )}
            </div>
          </div>

          <Messages messages={(d.messages ?? []) as Message[]} me="staff" onSend={sendMessage} busy={msgBusy} />
        </div>

        <div>
          <div className="panel">
            <div className="panel-head">
              <h3>Actions</h3>
            </div>
            <div className="panel-pad" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {nextStatus && (
                <button className="btn btn-primary" onClick={() => void advance(nextStatus)} disabled={update.isPending}>
                  Advance to {STATUS[nextStatus].label}
                </button>
              )}
              {sub.status === 'info_needed' && (
                <button className="btn btn-primary" onClick={() => void advance(resumeTo)} disabled={update.isPending}>
                  Resume — back to {STATUS[resumeTo].label}
                </button>
              )}
              {sub.status !== 'info_needed' && (
                <button className="btn btn-ghost" onClick={() => void markInfoNeeded()}>
                  Mark info needed
                </button>
              )}
              {sub.status !== 'declined' && (
                <button className="btn btn-ghost" onClick={() => void advance('declined')}>
                  Decline
                </button>
              )}
              {Number(d.completedAt) > 0 ? (
                <button className="btn btn-ghost" onClick={() => void reopenSubmission()} disabled={update.isPending}>
                  ↩ Reopen (undo completed)
                </button>
              ) : (
                <button className="btn btn-ghost" onClick={onCompleteClick} disabled={update.isPending}>
                  ✓ Mark completed
                </button>
              )}
              <div className="fgroup" style={{ margin: '2px 0 0' }}>
                <label className="flabel" style={{ fontSize: 11, color: 'var(--muted)' }}>Or set the stage manually</label>
                <select className="finput" value={sub.status} onChange={(e) => void setStage(e.target.value)}>
                  {(STAGE_CHOICES.includes(sub.status as SubmissionStatus)
                    ? STAGE_CHOICES
                    : [sub.status as SubmissionStatus, ...STAGE_CHOICES]
                  ).map((s) => (
                    <option key={s} value={s}>{STATUS[s]?.label ?? s}</option>
                  ))}
                </select>
              </div>
              <button className="btn btn-ghost" onClick={() => void markUnread()}>
                Mark as unread
              </button>
              <button className="btn btn-ghost" onClick={() => setShowDocReq(true)}>
                Request a document
              </button>
              <button
                className="btn btn-ghost"
                onClick={() => setPinConfirm(true)}
                title="Generate a new login PIN for the agent (they lost theirs). The code stays the same."
              >
                Reset agent PIN
              </button>
              <button
                className="btn btn-ghost"
                onClick={() => setShowMga(true)}
                title="Submitting to an MGA or a standard carrier's underwriter directly — not quoted in the portal"
              >
                <SendIcon width={16} height={16} /> Submit Manually
              </button>
              <hr style={{ border: 'none', borderTop: '1px solid var(--border)', margin: '4px 0' }} />
              <button
                className="btn btn-ghost"
                disabled={aiBusy}
                onClick={() => void generateNarrative()}
                title={narrativeGenAt ? 'Click to regenerate the ops & exec summary' : undefined}
              >
                <SparkIcon width={16} height={16} />{' '}
                {aiBusy
                  ? 'Generating…'
                  : narrativeGenAt
                    ? `✓ Generated ${new Date(narrativeGenAt).toLocaleDateString()}`
                    : 'Generate ops & exec summary'}
              </button>
              <button className="btn btn-accent" onClick={() => void syncSalesforce()}>
                <SendIcon width={16} height={16} /> Sync to Salesforce
              </button>
              {d.proposalPremium && (
                <button
                  className="btn btn-ghost"
                  onClick={async () => {
                    const { generateProposalPdf } = await import('../lib/pdf');
                    generateProposalPdf(sub.id, d);
                  }}
                >
                  <DownloadIcon width={16} height={16} /> Download proposal PDF
                </button>
              )}
            </div>
          </div>

          <div className="panel">
            <div className="panel-head">
              <h3>Completeness</h3>
            </div>
            <div className="panel-pad">
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <span className={`grade-badge grade-lg g-${grade.band}`}>{grade.grade}</span>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--navy)' }}>{grade.score}/100</div>
                  <div className="sub-meta">Staff-only — not shown to the agent.</div>
                </div>
              </div>
              {grade.boosts.length > 0 && (
                <div style={{ marginTop: 12 }}>
                  <div className="section-label" style={{ margin: '0 0 7px' }}>Helping</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {grade.boosts.map((b, i) => (
                      <span key={i} className="grade-chip good">✓ {b}</span>
                    ))}
                  </div>
                </div>
              )}
              {grade.gaps.length > 0 && (
                <div style={{ marginTop: 12 }}>
                  <div className="section-label" style={{ margin: '0 0 7px' }}>To improve</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {grade.gaps.map((b, i) => (
                      <span key={i} className="grade-chip gap">{b}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {isGatekeeper && (
            <GatekeeperPanel
              data={d}
              patch={patch}
              carriers={(gkCarriers.data ?? []).filter((c) => c.mode === 'email')}
              loading={gkCarriers.isLoading}
              onPrepare={(cs) => setGkEmail(cs)}
            />
          )}

          {isLiberty && (
            <GatekeeperPanel
              data={d}
              patch={patch}
              carriers={(gkCarriers.data ?? []).filter((c) => c.key === 'liberty')}
              loading={gkCarriers.isLoading}
              onPrepare={(cs) => setGkEmail(cs)}
              title="Liberty Mutual submission"
              subtitle="Direct-appointment submission — submit through the Liberty agent portal, then update the status."
            />
          )}

          {sub.status === 'bound' && (
            <div className="panel">
              <div className="panel-head">
                <h3>Bound &amp; commission</h3>
              </div>
              <div className="panel-pad" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div className="sub-meta" style={{ marginTop: -2 }}>
                  Enter the bound figures — these feed the COMM-PASS commission book.
                </div>
                <div className="fgroup" style={{ margin: 0 }}>
                  <label className="flabel">Bound premium</label>
                  <input
                    className="finput"
                    value={bound.boundPremium}
                    placeholder="$"
                    onChange={(e) => setBound((b) => ({ ...b, boundPremium: currencyInput(e.target.value) }))}
                  />
                </div>
                <div className="fgroup" style={{ margin: 0 }}>
                  <label className="flabel">Commission %</label>
                  <input
                    className="finput"
                    value={bound.commissionPct}
                    placeholder="e.g. 15"
                    onChange={(e) => setBound((b) => ({ ...b, commissionPct: pctInput(e.target.value) }))}
                  />
                </div>
                <div className="fgroup" style={{ margin: 0 }}>
                  <label className="flabel">Effective date</label>
                  <input
                    type="date"
                    className="finput"
                    value={bound.effectiveDate}
                    onChange={(e) => setBound((b) => ({ ...b, effectiveDate: e.target.value }))}
                  />
                </div>
                <div className="banner" style={{ margin: '2px 0 0', display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 13 }}>Total commission <b style={{ color: 'var(--navy)' }}>{money(boundTotalComm)}</b></span>
                  <span style={{ fontSize: 13 }}>You make <b style={{ color: 'var(--orange-deep)' }}>{money(boundYouMake)}</b> <span className="sub-meta" style={{ display: 'inline' }}>at {COMMPASS_PAYOUT_PCT}%</span></span>
                </div>
                <button className="btn btn-accent" onClick={() => void saveBound()} disabled={update.isPending}>
                  {update.isPending ? 'Saving…' : 'Save commission details'}
                </button>
              </div>
            </div>
          )}

          <ExtractPanel data={d} patch={patch} />

          {['received', 'review', 'quoting', 'mga_creating', 'mga_sent', 'info_needed', 'proposal_sent', 'bound'].includes(sub.status) && (
            <QuotesPanel data={d} patch={patch} id={sub.id} />
          )}

          {['review', 'quoting', 'mga_sent', 'info_needed'].includes(sub.status) && (
            <ProposalPanel data={d} patch={patch} id={sub.id} />
          )}

          {['proposal_sent'].includes(sub.status) && <PaymentPanel data={d} patch={patch} />}

          <div className="panel">
            <div className="panel-head">
              <h3>Timeline</h3>
            </div>
            <div className="panel-pad">
              {timeline.length === 0 && <div className="sub-meta">No status changes yet.</div>}
              {timeline.map((h, i) => (
                <div key={i} className="tl-item">
                  <div className="tl-dot" />
                  <div>
                    <b>{h.label}</b>
                    {h.sub && <div className="sub-meta">{h.sub}</div>}
                    <div className="sub-meta">{new Date(h.at).toLocaleString()}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {showDocReq && (
        <Modal
          title="Request a document from the agent"
          subtitle="Tell the agent which document(s) you need. They're added as outstanding requests, and the agent gets an email asking them to upload."
          onClose={() => setShowDocReq(false)}
          footer={
            <>
              <button className="btn btn-ghost" onClick={() => setShowDocReq(false)}>Cancel</button>
              <button className="btn btn-primary" disabled={reqSel.length === 0} onClick={() => void submitDocRequest()}>
                Request &amp; email agent
              </button>
            </>
          }
        >
          <label className="flabel">Document types</label>
          <div style={{ display: 'grid', gap: 6, margin: '6px 0 14px' }}>
            {DOC_REQUEST_TYPES.map((t) => (
              <label key={t} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
                <input
                  type="checkbox"
                  checked={reqSel.includes(t)}
                  onChange={() => setReqSel((s) => (s.includes(t) ? s.filter((x) => x !== t) : [...s, t]))}
                />
                {t}
              </label>
            ))}
          </div>
          {reqSel.includes(DISCOVERY_DOC_LABEL) && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', background: 'var(--green-soft)', border: '1px solid #B6E0C8', color: '#155F3E', borderRadius: 8, padding: '9px 11px', fontSize: 12.5, lineHeight: 1.45, margin: '0 0 14px' }}>
            <span>✓ The blank <b>Discovery form</b> will be included in this email for the agent to fill out and upload — you don't need to attach it yourself.</span>
          </div>
          )}
          <label className="flabel">Note to agent (optional)</label>
          <textarea
            className="finput"
            rows={2}
            placeholder="e.g. The carrier needs this before they'll release pricing."
            value={reqNote}
            onChange={(e) => setReqNote(e.target.value)}
          />
        </Modal>
      )}

      {showMga && <MgaModal data={d} patch={patch} id={sub.id} onClose={() => setShowMga(false)} />}

      {gkEmail && (
        <GatekeeperEmailModal data={d} patch={patch} id={sub.id} carriers={gkEmail} onClose={() => setGkEmail(null)} />
      )}

      {narrative && (
        <Modal
          title="Review AI draft"
          subtitle="Drafted from the website and uploaded documents. Edit either section before saving — nothing is written to the submission until you click Save."
          onClose={() => setNarrative(null)}
          footer={
            <>
              <button className="btn btn-ghost" onClick={() => setNarrative(null)}>Discard</button>
              <button
                className="btn btn-primary"
                disabled={(!narrative.descriptionOfOps.trim() && !narrative.executiveSummary.trim()) || update.isPending}
                onClick={() => void saveNarrative()}
              >
                {update.isPending ? 'Saving…' : 'Save to submission'}
              </button>
            </>
          }
        >
          <div
            style={{
              background: '#FEF3C7',
              border: '1px solid #F59E0B',
              borderRadius: 8,
              padding: '10px 12px',
              marginBottom: 14,
              fontSize: 13,
              lineHeight: 1.5,
              color: '#7C4A03',
            }}
          >
            <strong>⚠ AI draft — verify before sending to a carrier.</strong> Check every specific claim against
            the ACORDs and supplemental. AI can over-generalize the class or sector (e.g. calling well servicing
            an "oilfield/refinery" account). Correct anything that isn't stated in the source documents.
          </div>
          {narrative.verification && narrative.verification.length > 0 && (
            <div
              style={{
                background: '#FEF2F2',
                border: '1px solid #DC2626',
                borderRadius: 8,
                padding: '10px 12px',
                marginBottom: 14,
                fontSize: 13,
                lineHeight: 1.5,
                color: '#7F1D1D',
              }}
            >
              <strong>
                Accuracy check — {narrative.verification.length} claim
                {narrative.verification.length === 1 ? '' : 's'} not clearly supported by the documents. Verify or
                remove before saving:
              </strong>
              <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                {narrative.verification.map((v, i) => (
                  <li key={i} style={{ marginBottom: 4 }}>
                    <b>“{v.claim}”</b>
                    {v.note ? ` — ${v.note}` : ''}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {narrative.verification && narrative.verification.length === 0 && (
            <div
              style={{
                background: '#DCFCE7',
                border: '1px solid #16A34A',
                borderRadius: 8,
                padding: '8px 12px',
                marginBottom: 14,
                fontSize: 12.5,
                color: '#14532D',
              }}
            >
              ✓ Accuracy check: every claim traces to the source documents/facts. Still confirm anything the form
              itself may state incorrectly.
            </div>
          )}
          <div className="fgroup" style={{ marginBottom: 12 }}>
            <label className="flabel">Description of operations</label>
            <textarea
              className="finput"
              rows={7}
              value={narrative.descriptionOfOps}
              onChange={(e) => setNarrative((s) => (s ? { ...s, descriptionOfOps: e.target.value } : s))}
            />
          </div>
          <div className="fgroup" style={{ marginBottom: 0 }}>
            <label className="flabel">Executive summary</label>
            <textarea
              className="finput"
              rows={6}
              value={narrative.executiveSummary}
              onChange={(e) => setNarrative((s) => (s ? { ...s, executiveSummary: e.target.value } : s))}
            />
          </div>
        </Modal>
      )}

      {pinConfirm && (
        <Modal
          title="Reset agent PIN"
          onClose={() => setPinConfirm(false)}
          footer={
            <>
              <button className="btn btn-ghost" onClick={() => setPinConfirm(false)}>
                Cancel
              </button>
              <button className="btn btn-primary" disabled={pinBusy} onClick={() => void doResetPin()}>
                {pinBusy ? 'Resetting…' : 'Reset PIN'}
              </button>
            </>
          }
        >
          <p style={{ fontSize: 14, lineHeight: 1.55 }}>
            Generate a new 4-digit login PIN for <b>{sub.business_name || sub.id}</b>. The current PIN
            stops working immediately, and the agent keeps the same code <b>{sub.id}</b>. You'll get the
            new PIN to pass along.
          </p>
        </Modal>
      )}

      {newPin && (
        <Modal title="New PIN — send this to the agent" onClose={() => setNewPin(null)} footer={
          <button className="btn btn-primary" onClick={() => setNewPin(null)}>Done</button>
        }>
          <p style={{ fontSize: 14, lineHeight: 1.55, marginBottom: 12 }}>
            Give the agent both of these to sign in and upload documents:
          </p>
          <div style={{ display: 'flex', gap: 24, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--r-sm)', padding: '14px 16px' }}>
            <div>
              <div className="sub-meta">Code</div>
              <div style={{ fontSize: 20, fontWeight: 700, letterSpacing: 0.5 }}>{sub.id}</div>
            </div>
            <div>
              <div className="sub-meta">New PIN</div>
              <div style={{ fontSize: 20, fontWeight: 700, letterSpacing: 2 }}>{newPin}</div>
            </div>
          </div>
          <p className="sub-meta" style={{ marginTop: 10 }}>
            The old PIN no longer works. This PIN won't be shown again — if you lose it, just reset again.
          </p>
        </Modal>
      )}

      {showComplete && (() => {
        const needsNote = compReason === 'Other';
        const finalReason = needsNote ? compNote.trim() : compReason;
        const ready = !!compReason && (!needsNote || compNote.trim().length > 0);
        return (
          <Modal
            title="Why is this being completed?"
            onClose={() => setShowComplete(false)}
            footer={
              <>
                <button className="btn btn-ghost" onClick={() => setShowComplete(false)}>Cancel</button>
                <button
                  className="btn btn-primary"
                  disabled={!ready || update.isPending}
                  onClick={() => void completeSubmission(finalReason)}
                >
                  {update.isPending ? 'Completing…' : 'Complete submission'}
                </button>
              </>
            }
          >
            <p style={{ fontSize: 14, lineHeight: 1.55, marginBottom: 12 }}>
              <b>{sub.business_name || sub.id}</b> didn't bind. Pick a reason so we always know why it
              was closed — it shows on the card and in the timeline.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {COMPLETE_REASONS.map((r) => (
                <label key={r} style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 14, cursor: 'pointer' }}>
                  <input type="radio" name="detail-complete-reason" checked={compReason === r} onChange={() => setCompReason(r)} />
                  {r}
                </label>
              ))}
            </div>
            {needsNote && (
              <input
                className="finput"
                style={{ marginTop: 10 }}
                placeholder="Briefly, what happened?"
                value={compNote}
                onChange={(e) => setCompNote(e.target.value)}
                autoFocus
              />
            )}
          </Modal>
        );
      })()}
    </div>
  );
}
